import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { sitesApi } from '../../components/sites/sitesApi';
import FactsPanel from '../../components/sites/FactsPanel';
import MediaPanel from '../../components/sites/MediaPanel';
import DesignGallery from '../../components/sites/DesignGallery';
import PublishPanel from '../../components/sites/PublishPanel';
import { countConfirmedForUi, countFactsForUi } from '../../components/sites/factsPanelUtils';
import { allFactPathsConfirmed, publishHeaderTooltip } from '../../components/sites/publishPanelUtils';
import { setBriefPathValue } from '../../components/sites/briefPathUtils';

const tabs = ['Facts', 'Logo & photos', 'Design'];

export default function SiteEditor() {
  const { siteId } = useParams();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const isMobile = useIsMobile();
  const [docWorkspaceId, setDocWorkspaceId] = useState(location.state?.workspaceId || null);
  const workspaceId = docWorkspaceId || currentUser?.uid || selectedWorkspaceId;

  const [activeTab, setActiveTab] = useState('Facts');
  const [previewUrl, setPreviewUrl] = useState(location.state?.previewUrl || '');
  const [facts, setFacts] = useState(location.state?.facts || []);
  const [brief, setBrief] = useState(location.state?.brief || null);
  const [photos, setPhotos] = useState(location.state?.photos || []);
  const [confirmedPaths, setConfirmedPaths] = useState([]);
  const [ranking, setRanking] = useState(location.state?.report?.ranking || []);
  const [currentLook, setCurrentLook] = useState(location.state?.look || null);
  const [view, setView] = useState('desktop');
  const [publishOpen, setPublishOpen] = useState(false);
  const [ownership, setOwnership] = useState(null);
  const [domain, setDomain] = useState(null);
  const [published, setPublished] = useState(null);
  const [factsReadyToPublish, setFactsReadyToPublish] = useState(false);
  const [siteVersions, setSiteVersions] = useState([]);
  const [editorLoaded, setEditorLoaded] = useState(false);
  /** In-flight inline fact edits: keep UI stable until Firestore reflects the new value. */
  const pendingFactEditsRef = useRef({});

  useEffect(() => {
    if (location.hash === '#logo-photos') {
      setActiveTab('Logo & photos');
    }
  }, [location.hash]);

  const applyEditorPayload = useCallback((data) => {
    if (!data) return;
    if (data.workspaceId) setDocWorkspaceId(data.workspaceId);
    if (data.facts?.length) setFacts(data.facts);
    if (data.brief) setBrief(data.brief);
    if (Array.isArray(data.confirmedFactPaths)) setConfirmedPaths(data.confirmedFactPaths);
    if (data.ownership) setOwnership(data.ownership);
    else if (data.ownership === null) setOwnership(null);
    if (data.domain) setDomain(data.domain);
    else if (data.domain === null) setDomain(null);
    if (data.published) setPublished(data.published);
    else if (data.published === null) setPublished(null);
    if (data.previewUrl) setPreviewUrl(data.previewUrl);
    if (data.look) setCurrentLook(data.look);
    if (Array.isArray(data.photoCatalog)) setPhotos(data.photoCatalog);
    if (data.report?.ranking) setRanking(data.report.ranking);
    if (Array.isArray(data.versions)) setSiteVersions(data.versions);
    setFactsReadyToPublish(allFactPathsConfirmed(data.facts || [], data.confirmedFactPaths || [], data.brief));
  }, []);

  const refreshEditorSite = useCallback(async () => {
    if (!siteId) return false;
    const ws = workspaceId || currentUser?.uid;
    if (!ws) return false;
    try {
      const data = await sitesApi.getEditorSite(ws, siteId);
      applyEditorPayload(data);
      setEditorLoaded(true);
      return true;
    } catch {
      return false;
    }
  }, [workspaceId, siteId, currentUser?.uid, applyEditorPayload]);

  const handlePublishSuccess = useCallback(
    (res) => {
      if (res?.version == null) return;
      let host = domain?.host;
      if (!host && res.url) {
        try {
          host = new URL(res.url).hostname;
        } catch {
          /* ignore */
        }
      }
      if (host) setPublished({ version: Number(res.version), host });
    },
    [domain?.host],
  );

  useEffect(() => {
    refreshEditorSite();
  }, [refreshEditorSite]);

  useEffect(() => {
    const disableStrayBodyIframes = () => {
      for (const iframe of document.querySelectorAll('body > iframe')) {
        iframe.style.pointerEvents = 'none';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.position = 'fixed';
        iframe.style.opacity = '0';
      }
    };
    disableStrayBodyIframes();
    const obs = new MutationObserver(disableStrayBodyIframes);
    obs.observe(document.body, { childList: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (location.state?.photos?.length || location.state?.facts?.length) return;
    try {
      const raw = sessionStorage.getItem(`sites-handoff-${siteId}`);
      if (!raw) return;
      const handoff = JSON.parse(raw);
      if (handoff.previewUrl) setPreviewUrl(handoff.previewUrl);
      if (handoff.facts?.length) setFacts(handoff.facts);
      if (handoff.brief) setBrief(handoff.brief);
      if (handoff.photos?.length) setPhotos(handoff.photos);
      if (handoff.report?.ranking) setRanking(handoff.report.ranking);
      if (handoff.workspaceId) setDocWorkspaceId(handoff.workspaceId);
    } catch {
      /* ignore */
    }
  }, [siteId, location.state?.facts?.length, location.state?.photos?.length]);

  useEffect(() => {
    if (location.state?.photos?.length) setPhotos(location.state.photos);
  }, [location.state?.photos]);

  useEffect(() => {
    if (!siteId) return undefined;
    if (process.env.REACT_APP_USE_EMULATORS === 'true') {
      return undefined;
    }
    const unsub = onSnapshot(doc(db, 'sites', siteId), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.workspaceId) setDocWorkspaceId(d.workspaceId);
      if (d.facts?.length) {
        setFacts(
          d.facts.map((f) => {
            const pending = pendingFactEditsRef.current[f.path];
            if (pending === undefined) return f;
            const serverVal = f.value;
            const matches =
              String(serverVal ?? '') === String(pending) ||
              JSON.stringify(serverVal) === JSON.stringify(pending);
            if (matches) delete pendingFactEditsRef.current[f.path];
            return { ...f, value: pending, origin: 'owner' };
          })
        );
      }
      if (d.brief) {
        let nextBrief = d.brief;
        const pending = pendingFactEditsRef.current;
        if (Object.keys(pending).length) {
          nextBrief = JSON.parse(JSON.stringify(d.brief));
          for (const [p, v] of Object.entries(pending)) {
            setBriefPathValue(nextBrief, p, v);
          }
        }
        setBrief(nextBrief);
      }
      if (Array.isArray(d.photoCatalog)) {
        setPhotos(d.photoCatalog);
      }
      if (Array.isArray(d.confirmedFactPaths)) {
        setConfirmedPaths((prev) => [...new Set([...(d.confirmedFactPaths || []), ...prev])]);
      }
      if (d.facts?.length) {
        setFactsReadyToPublish(allFactPathsConfirmed(d.facts, d.confirmedFactPaths || [], d.brief));
      }
      if (d.previewUrl) setPreviewUrl(d.previewUrl);
      if (d.look) setCurrentLook(d.look);
      if (d.report?.ranking) setRanking(d.report.ranking);
      else if (d.ranking) setRanking(d.ranking);
      if (d.ownership) setOwnership(d.ownership);
      if (d.domain) setDomain(d.domain);
      if (d.published) setPublished(d.published);
      else if (d.published === null) setPublished(null);
    });
    return () => unsub();
  }, [siteId]);

  const totalFacts = countFactsForUi(facts, brief);
  const confirmedCount = countConfirmedForUi(facts, confirmedPaths, brief);
  const allConfirmed =
    factsReadyToPublish ||
    allFactPathsConfirmed(facts, confirmedPaths, brief) ||
    (totalFacts > 0 && confirmedCount >= totalFacts);

  function handleConfirm(path) {
    if (confirmedPaths.includes(path)) return;
    const next = [...confirmedPaths, path];
    flushSync(() => setConfirmedPaths(next));
    if (workspaceId && siteId) {
      sitesApi.updateSite(workspaceId, siteId, { confirmedFactPaths: next }).catch(() => {});
    }
  }

  async function handleEditValue(path, value) {
    if (!brief) return;
    pendingFactEditsRef.current[path] = value;
    const nextBrief = JSON.parse(JSON.stringify(brief));
    setBriefPathValue(nextBrief, path, value);
    const nextConfirmed = confirmedPaths.includes(path) ? confirmedPaths : [...confirmedPaths, path];
    flushSync(() => {
      setBrief(nextBrief);
      setFacts((prev) => prev.map((f) => (f.path === path ? { ...f, value, origin: 'owner' } : f)));
      setConfirmedPaths(nextConfirmed);
    });
    try {
      await sitesApi.updateSite(workspaceId, siteId, { brief: nextBrief, confirmedFactPaths: nextConfirmed });
    } catch {
      delete pendingFactEditsRef.current[path];
    }
  }

  async function handleBriefMediaChange(nextBrief) {
    setBrief(nextBrief);
  }

  const previewWidth = view === 'phone' ? 390 : '100%';
  const publishTooltip = publishHeaderTooltip({
    editorLoaded,
    allFactsConfirmed: allConfirmed,
    ownership,
    domain,
    publishOpen,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden', maxWidth: '100vw', overflowX: 'hidden' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1.25rem',
          borderBottom: '1px solid #e5e7eb',
          background: '#fff',
          flexShrink: 0,
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.125rem', margin: 0 }}>{brief?.business?.name || 'Your site'}</h1>
          {totalFacts > 0 && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>
              {confirmedCount} of {totalFacts} facts confirmed
            </p>
          )}
          {(brief?.people || []).length > 0 && (
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
              {(brief.people || []).map((p) => p.name).filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={(!allConfirmed || !editorLoaded) && !publishOpen}
          title={publishTooltip}
          aria-label={publishTooltip}
          onClick={() => {
            if ((!allConfirmed || !editorLoaded) && !publishOpen) return;
            setPublishOpen((o) => !o);
          }}
          style={{
            padding: '0.6rem 1.25rem',
            borderRadius: 999,
            border: 'none',
            background: (allConfirmed && editorLoaded) || publishOpen ? 'var(--ink, #171A0D)' : '#9ca3af',
            color: '#fff',
            fontWeight: 600,
            cursor: (allConfirmed && editorLoaded) || publishOpen ? 'pointer' : 'not-allowed',
          }}
        >
          {publishOpen ? 'Close' : 'Publish'}
        </button>
      </header>
      <PublishPanel
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        workspaceId={workspaceId}
        siteId={siteId}
        brief={brief}
        allFactsConfirmed={factsReadyToPublish || allFactPathsConfirmed(facts, confirmedPaths, brief)}
        ownership={ownership}
        domain={domain}
        published={published}
        versions={siteVersions}
        onSiteUpdated={refreshEditorSite}
        onVersionsChange={setSiteVersions}
        onPublishSuccess={handlePublishSuccess}
      />
      <div
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: isMobile ? 'column' : 'row',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            flex: isMobile ? 'none' : '3 1 0',
            minWidth: isMobile ? 0 : '66%',
            minHeight: 0,
            background: '#f3f4f6',
            display: 'flex',
            flexDirection: 'column',
            padding: '0.75rem',
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
          }}
        >
          <div style={{ marginBottom: '0.5rem' }}>
            <button type="button" onClick={() => setView('desktop')} style={{ marginRight: 8, fontWeight: view === 'desktop' ? 700 : 400 }}>Desktop</button>
            <button type="button" onClick={() => setView('phone')} style={{ fontWeight: view === 'phone' ? 700 : 400 }}>Phone</button>
          </div>
          {previewUrl ? (
          <iframe
            key={previewUrl}
            title="Site preview"
            src={previewUrl}
            style={{
              flex: 1,
              width: previewWidth,
              maxWidth: '100%',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              background: '#fff',
              minHeight: 320,
            }}
          />
        ) : (
          <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 'auto' }}>Loading preview…</p>
        )}
        </div>
        <aside
          style={{
            flex: isMobile ? '1 1 auto' : '1 1 0',
            maxWidth: isMobile ? '100%' : activeTab === 'Design' ? 520 : 360,
            minWidth: isMobile ? 0 : activeTab === 'Design' ? 420 : 280,
            borderLeft: isMobile ? 'none' : '1px solid #e5e7eb',
            borderTop: isMobile ? '1px solid #e5e7eb' : 'none',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            zIndex: 50,
          }}
        >
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={activeTab === t}
                onClick={() => setActiveTab(t)}
                style={{
                  flex: 1,
                  padding: '0.65rem 0.5rem',
                  border: 'none',
                  borderBottom: activeTab === t ? '2px solid #171A0D' : '2px solid transparent',
                  background: '#fff',
                  fontWeight: activeTab === t ? 700 : 500,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                {t}
              </button>
            ))}
          </div>
          {photos.length > 0 && activeTab === 'Facts' ? (
            <p style={{ margin: 0, padding: '0.5rem 0.65rem', fontSize: '0.8rem', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>
              <a
                href="#logo-photos"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab('Logo & photos');
                  window.history.replaceState(null, '', `${location.pathname}${location.search}#logo-photos`);
                }}
                style={{ color: '#171A0D', fontWeight: 600 }}
              >
                Logo &amp; photos
              </a>
              {' '}
              to copy images from your current site.
            </p>
          ) : null}
          <div style={{ flex: 1, overflow: 'auto', padding: '0.75rem 0.5rem' }}>
            <div style={{ display: activeTab === 'Facts' ? 'block' : 'none' }}>
              <FactsPanel facts={facts} brief={brief} confirmedPaths={confirmedPaths} onConfirm={handleConfirm} onEditValue={handleEditValue} />
            </div>
            <div style={{ display: activeTab === 'Logo & photos' ? 'block' : 'none' }}>
              {!editorLoaded || !brief ? (
                <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading logo and photos…</p>
              ) : (
                <MediaPanel
                  workspaceId={workspaceId}
                  siteId={siteId}
                  brief={brief}
                  photos={photos}
                  currentLook={currentLook}
                  onBriefChange={handleBriefMediaChange}
                  onPreviewUrl={setPreviewUrl}
                  previewUrl={previewUrl}
                />
              )}
            </div>
            <div style={{ display: activeTab === 'Design' ? 'block' : 'none' }}>
              <DesignGallery
                workspaceId={workspaceId}
                siteId={siteId}
                ranking={ranking}
                brief={brief}
                selectedLook={currentLook}
                onPickLook={setCurrentLook}
                onPreviewUrl={setPreviewUrl}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
